import { useCallback } from 'react'

import { Pencil, ScalesBalanced, TrashBin } from '@gravity-ui/icons'
import { Button, Table, Tooltip } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { formatPLN } from '@/utils/currency'
import { formatDate } from '@/utils/dates'

interface InitialBalanceRowProps {
  id: string;
  startDate: string;
  total: number;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export function InitialBalanceRow({
  id,
  startDate,
  total,
  onEdit,
  onDelete,
}: InitialBalanceRowProps) {
  const { t } = useTranslation()

  const handleEdit = useCallback(() => {
    if (onEdit) {
      onEdit(id)
    }
  }, [id, onEdit])

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete(id)
    }
  }, [id, onDelete])

  return (
    <Table.Row key={id}>
      <Table.Cell>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-primary/10 text-primary dark:bg-primary/20 font-bold border border-primary/20">
          <ScalesBalanced className="size-4" />
        </div>
      </Table.Cell>
      <Table.Cell>
        <span className="font-semibold text-primary">
          {formatDate(startDate)}
        </span>
      </Table.Cell>
      <Table.Cell>
        <span className="font-bold text-primary">
          {t('settlements.type_initial_balance')}
        </span>
      </Table.Cell>
      <Table.Cell className="text-right font-black tabular-nums">
        <span className="text-primary">
          {total >= 0 ? '+' : '-'} {formatPLN(Math.abs(total))} PLN
        </span>
      </Table.Cell>
      <Table.Cell>
        <div className="flex justify-end gap-2">
          <Tooltip delay={0}>
            <Button
              isIconOnly
              size="sm"
              variant="tertiary"
              onClick={handleEdit}
              aria-label={t('common.edit')}
            >
              <Pencil className="size-4 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200" />
            </Button>
            <Tooltip.Content>
              <p>{t('common.edit')}</p>
            </Tooltip.Content>
          </Tooltip>

          <Tooltip delay={0}>
            <Button
              isIconOnly
              size="sm"
              variant="danger-soft"
              onClick={handleDelete}
              aria-label={t('common.delete')}
            >
              <TrashBin className="size-4 text-rose-500 hover:text-rose-700 dark:hover:text-rose-400" />
            </Button>
            <Tooltip.Content>
              <p>{t('common.delete')}</p>
            </Tooltip.Content>
          </Tooltip>
        </div>
      </Table.Cell>
    </Table.Row>
  )
}
